import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrainCircuit, Loader2 } from 'lucide-react';
import { organizationApi as orgApi, programApi, trainingApi } from '../services/api';

/**
 * Establish a new model for training (EP-26 FE-17 ST-2). One form creates the programme
 * (kind model-training) with its training profile; the server then creates its training
 * project, binds the training workflows and makes its three duty groups. Members are
 * added last (that needs group:manage). Everything here can be changed later on the
 * programme's Profile tab.
 */
const STORE_ROLES = ['units', 'knowledge', 'evidence', 'gold', 'runs', 'models', 'gateEvents', 'fitness', 'profiles', 'answerKey', 'graph', 'facts'];
const DUTIES: { key: string; label: string; help: string }[] = [
  { key: 'operators', label: 'Operators', help: 'run campaigns, batches, activities and runs' },
  { key: 'approvers', label: 'Approvers', help: 'approve spend, data releases, holds and the profile (two people)' },
  { key: 'model-release', label: 'Model release', help: 'clear and promote models' },
];
const input = 'mt-0.5 block w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600';
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 24) || 'model';

export default function TrainingNewPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<any[]>([]);
  const [f, setF] = useState<Record<string, any>>({
    organizationId: '', name: '', description: '', unitName: '', unitLabel: '',
    mainScoreName: 'within-1', mainScoreMin: 0.85, unsafeMax: 0.05, minEvidence: 100,
    mainScoreDrop: 0.02, unsafeRise: 0, rowsPerUnit: 90, targets: '', monthlyBudget: 0,
  });
  const [stores, setStores] = useState<Record<string, string>>({});
  const [members, setMembers] = useState<Record<string, string>>({ operators: '', approvers: '', 'model-release': '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const key = useMemo(() => slug(f.name), [f.name]);

  useEffect(() => { orgApi.list().then((o) => { setOrgs(o); if (o.length === 1) setF((x) => ({ ...x, organizationId: o[0]._id })); }).catch(() => {}); }, []);

  const set = (k: string, v: any) => setF((x) => ({ ...x, [k]: v }));
  const field = (label: string, k: string, type = 'text', help = '') => (
    <label className="text-xs text-gray-600 dark:text-gray-400 block">{label}
      <input className={input} type={type} step="any" value={f[k]} onChange={(e) => set(k, type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
      {help && <span className="block text-[11px] text-gray-400">{help}</span>}
      {errors[k] && <span className="block text-[11px] text-red-600">{errors[k]}</span>}
    </label>
  );

  const create = async () => {
    setBusy(true); setErrors({}); setMessage('');
    const profile: Record<string, any> = {
      unit: { name: f.unitName.trim(), label: f.unitLabel.trim() || f.unitName.trim() },
      passMarks: { mainScoreName: f.mainScoreName, mainScoreMin: Number(f.mainScoreMin), unsafeMax: Number(f.unsafeMax), minEvidence: Number(f.minEvidence) || 0 },
      regressionTolerance: { mainScoreDrop: Number(f.mainScoreDrop), unsafeRise: Number(f.unsafeRise) },
      depth: f.rowsPerUnit ? { rowsPerUnit: Number(f.rowsPerUnit) } : null,
      stores: Object.fromEntries(Object.entries(stores).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])),
      credentials: { read: `secret/pmo/training/${key}/read`, write: `secret/pmo/training/${key}/write` },
      compute: { targets: String(f.targets).split(',').map((s: string) => s.trim()).filter(Boolean), monthlyBudget: Number(f.monthlyBudget) || 0 },
    };
    try {
      const created: any = await programApi.create({ organizationId: f.organizationId, name: f.name.trim(), description: f.description,
        kind: 'model-training', customFields: { training: profile } });
      const notes: string[] = [];
      for (const d of DUTIES) {
        const emails = members[d.key].split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
        if (!emails.length) continue;
        try { await trainingApi.setGroupMembers(created._id, d.key, emails); } catch (e: any) { notes.push(`${d.label}: ${e?.message || 'not set'}`); }
      }
      if (notes.length) window.alert(`Created. Some members were not added:\n${notes.join('\n')}\nAdd them later from the programme.`);
      navigate(`/training/${encodeURIComponent(created._id)}`);
    } catch (e: any) {
      const d = e?.detail || {};
      const flat: Record<string, string> = {};
      const walk = (x: any, p: string) => {
        if (Array.isArray(x) && x.every((v) => typeof v === 'string')) flat[p] = x.join(' ');
        else if (x && typeof x === 'object') Object.entries(x).forEach(([k, v]) => walk(v, p ? `${p}.${k}` : k));
      };
      walk(d.errors || {}, '');
      const map: Record<string, string> = { 'customFields.training.unit.name': 'unitName', 'customFields.training.passMarks.mainScoreMin': 'mainScoreMin',
        'customFields.training.passMarks.unsafeMax': 'unsafeMax', name: 'name', organizationId: 'organizationId' };
      setErrors(Object.fromEntries(Object.entries(flat).map(([k, v]) => [map[k] || k, v])));
      setMessage(e?.message || 'The programme was not created.');
    } finally {
      setBusy(false);
    }
  };

  const ready = f.organizationId && f.name.trim() && f.unitName.trim();
  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> New model for training</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Creates a training programme with its own training project, the training workflows, and its operator, approver and
        model-release groups. Needs program:manage at the organisation. Everything can be changed later on the Profile tab.
      </p>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 grid gap-3 md:grid-cols-2">
        <h3 className="md:col-span-2 text-sm font-semibold">1. The model</h3>
        <label className="text-xs text-gray-600 dark:text-gray-400 block">Organisation
          <select className={input} value={f.organizationId} onChange={(e) => set('organizationId', e.target.value)}>
            <option value="">Choose…</option>{orgs.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
          </select>{errors.organizationId && <span className="block text-[11px] text-red-600">{errors.organizationId}</span>}
        </label>
        {field('Name', 'name', 'text', `Key: ${key}`)}
        <label className="md:col-span-2 text-xs text-gray-600 dark:text-gray-400 block">What the model is for
          <textarea className={`${input} h-16`} value={f.description} onChange={(e) => set('description', e.target.value)} /></label>
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 grid gap-3 md:grid-cols-2">
        <h3 className="md:col-span-2 text-sm font-semibold">2. Training unit and pass marks</h3>
        {field('Unit name', 'unitName', 'text', 'What one unit of training is, e.g. atom, skill, intent')}
        {field('Unit label', 'unitLabel')}
        {field('Main score name', 'mainScoreName')}
        {field('Main score minimum', 'mainScoreMin', 'number', 'A unit passes at or above this (0-1)')}
        {field('Unsafe maximum', 'unsafeMax', 'number')}
        {field('Minimum evidence per unit', 'minEvidence', 'number')}
        {field('Allowed main score drop between rounds', 'mainScoreDrop', 'number')}
        {field('Rows per unit in a round corpus', 'rowsPerUnit', 'number')}
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
        <h3 className="text-sm font-semibold">3. Stores and compute</h3>
        <p className="text-xs text-gray-500">The programme's CouchDB databases, one per role (leave empty what it does not use; new ones are provisioned as a platform task).
          Credentials are read from <code>secret/pmo/training/{key}/read</code> and <code>/write</code>.</p>
        <div className="grid gap-2 md:grid-cols-3">
          {STORE_ROLES.map((r) => (
            <label key={r} className="text-xs text-gray-600 dark:text-gray-400 block">{r}
              <input className={input} placeholder={`${key}_${r.toLowerCase()}_train_db`} value={stores[r] || ''}
                onChange={(e) => setStores((x) => ({ ...x, [r]: e.target.value }))} />
              {errors[`customFields.training.stores.${r}`] && <span className="block text-[11px] text-red-600">{errors[`customFields.training.stores.${r}`]}</span>}
            </label>))}
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {field('Compute targets', 'targets', 'text', 'Comma-separated, e.g. a4500, lambda, laptop')}
          {field('Monthly budget (USD)', 'monthlyBudget', 'number')}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
        <h3 className="text-sm font-semibold">4. Who works on it</h3>
        <p className="text-xs text-gray-500">PMO users' emails, comma-separated. Membership is what grants each duty; nobody is an approver by name. Needs group:manage.</p>
        {DUTIES.map((d) => (
          <label key={d.key} className="text-xs text-gray-600 dark:text-gray-400 block">{d.label} <span className="text-gray-400">({d.help})</span>
            <input className={input} value={members[d.key]} onChange={(e) => setMembers((x) => ({ ...x, [d.key]: e.target.value }))} /></label>))}
      </section>

      {message && <p className="text-sm text-red-600">{message}</p>}
      <button type="button" disabled={!ready || busy} onClick={create}
        className="px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50 flex items-center gap-1">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create the training programme</button>
    </div>
  );
}
